import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { FilterChips } from '@components/Filter/FilterChips';
import { Container } from '@components/Filter';
import { FlexRow } from '@components/FlexRow';
import InputAutocomplete from '@components/InputAutocomplete';
import InputDateRange from '@components/InputDateRange';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import { useAuth } from '@contexts/AuthContext';
import { Box, Button, FormGroup, FormLabel } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { hasValue } from '@components/Filter/filter.utils';
import { useGetJobOfferTemplateAvailableFilters } from './hooks/useGetJobOfferTemplateAvailableFilters';

export type IJobOfferTemplateFilter = {
    proId?: string[];
    brandId?: string[];
    createdAt?: {
        start: string | null;
        end: string | null;
    };
};

export const defaultTemplateFilters: IJobOfferTemplateFilter = {
    proId: [],
    brandId: [],
    createdAt: {
        start: null,
        end: null,
    },
};

const filterLabels = {
    proId: 'Entreprise',
    brandId: 'Marque',
    createdAt: 'Date de création',
};

export const JobOfferTemplateFilter = ({
    totalDocs,
    query: searchQuery = '',
    onQueryChange,
    onFilterChange,
    onAddTemplate,
    filter,
    lockedProId,
}: {
    totalDocs: number;
    query?: string;
    onQueryChange: (query: string) => void;
    onFilterChange: (filter: IJobOfferTemplateFilter) => void;
    onAddTemplate?: () => void;
    filter?: IJobOfferTemplateFilter;
    lockedProId?: string;
}) => {
    const { isUpercut } = useAuth();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [chipLabels, setChipLabels] = useState<Record<string, any>>({
        proId: [],
        brandId: [],
    });

    const methods = useForm({
        defaultValues: defaultTemplateFilters,
    });

    const { availableFilters } = useGetJobOfferTemplateAvailableFilters(searchQuery, filter ?? defaultTemplateFilters);

    const proOptions = availableFilters.proIds.map((opt) => ({
        label: `${opt.label} (${opt.count})`,
        value: opt.value,
    }));

    const brandOptions = availableFilters.brandIds.map((opt) => ({
        label: `${opt.label} (${opt.count})`,
        value: opt.value,
    }));

    const toggleDrawer = (open: boolean) => () => {
        setIsDrawerOpen(open);
    };

    const onApplyFilter = (data: IJobOfferTemplateFilter) => {
        onFilterChange(data);
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        onFilterChange(defaultTemplateFilters);
        methods.reset(defaultTemplateFilters);
        setIsDrawerOpen(false);
    };

    const hasActiveFilters = Object.entries(filter || {}).some(([, value]) => hasValue(value));

    return (
        <FormProvider {...methods}>
            <Container
                sx={{
                    gap: 1,
                    flexWrap: 'wrap',
                }}>
                <FlexRow sx={{ flex: 1, minWidth: 0, alignItems: 'center', gap: 1 }}>
                    <SearchBarWithNumber
                        onChange={onQueryChange}
                        placeholder="Rechercher un modèle..."
                        totalCount={totalDocs}
                    />
                    <FilterButton toggleDrawer={toggleDrawer} hasActiveFilters={hasActiveFilters} />
                </FlexRow>
                {onAddTemplate && (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<i className="fi fi-br-plus" style={{ fontSize: 12 }} />}
                        onClick={onAddTemplate}
                        sx={{ borderRadius: '12px', textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                        Créer un modèle
                    </Button>
                )}
            </Container>

            {hasActiveFilters && (
                <Container
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        '&::-webkit-scrollbar': { height: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: theme => theme.palette.neutralDarkGray.main,
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: theme => theme.palette.neutralGray.main,
                        },
                    }}>
                    <FlexRow sx={{ minWidth: 'fit-content', paddingBottom: '4px' }}>
                        <FilterChips
                            filter={filter}
                            labels={filterLabels}
                            defaultFilters={defaultTemplateFilters}
                            onFilterChange={onFilterChange}
                            chipLabels={chipLabels}
                            stackProps={{ flexWrap: 'nowrap' }}
                            hideKeys={lockedProId ? ['proId'] : []}
                        />
                    </FlexRow>
                </Container>
            )}

            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}>
                <FormGroup>
                    <FormLabel>{filterLabels.createdAt}</FormLabel>
                    <InputDateRange name="createdAt" />
                </FormGroup>
                {isUpercut && !lockedProId && (
                    <FormGroup>
                        <FormLabel>{filterLabels.proId}</FormLabel>
                        <InputAutocomplete
                            id="proId"
                            name="proId"
                            multiple
                            options={proOptions}
                            onChange={() => {
                                setChipLabels(prev => ({
                                    ...prev,
                                    proId: proOptions,
                                }));
                            }}
                        />
                    </FormGroup>
                )}
                <FormGroup>
                    <FormLabel>{filterLabels.brandId}</FormLabel>
                    <InputAutocomplete
                        id="brandId"
                        name="brandId"
                        multiple
                        options={brandOptions}
                        onChange={() => {
                            setChipLabels(prev => ({
                                ...prev,
                                brandId: brandOptions,
                            }));
                        }}
                    />
                </FormGroup>
            </FilterDrawer>
        </FormProvider>
    );
};

export default JobOfferTemplateFilter;
